import React from 'react';
import { 
  Users, FileText, CheckCircle, Settings, Shield, 
  Clock, Eye, Edit, Building2, GraduationCap, 
  BarChart3, Bell, MessageSquare, UserCog, Target, 
  TrendingUp, AlertTriangle, Award, Sparkles, PlusCircle,
  MapPin, Calendar, Timer, DollarSign
} from 'lucide-react';

// Reusable slide content components
const FeatureList = ({ items }) => (
  <ul className="space-y-3">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-3">
        <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
        <span className="text-gray-600">{item}</span>
      </li>
    ))}
  </ul>
);

const StepList = ({ steps }) => (
  <div className="space-y-4">
    {steps.map((step, i) => (
      <div key={i} className="flex items-start gap-4">
        <div className="w-8 h-8 rounded-full bg-teal-500 flex items-center justify-center text-gray-800 font-semibold flex-shrink-0">
          {i + 1}
        </div>
        <div>
          <p className="font-medium text-gray-800">{step.title}</p>
          {step.description && <p className="text-sm text-gray-500 mt-1">{step.description}</p>}
        </div>
      </div>
    ))}
  </div>
);

const PermissionCard = ({ name, description, enabled }) => (
  <div className={`rounded-xl p-3 border ${enabled ? 'bg-green-500/10 border-green-500/30' : 'bg-gray-100 border-gray-200'}`}>
    <div className="flex items-center justify-between">
      <p className="font-medium text-gray-800">{name}</p>
      <div className={`w-3 h-3 rounded-full ${enabled ? 'bg-green-500' : 'bg-gray-600'}`} />
    </div>
    <p className="text-xs text-gray-500 mt-1">{description}</p>
  </div>
);

export const employeeTrainingSlides = [
  // Slide 1: Welcome
  {
    title: "Welcome, Team Member!",
    subtitle: "Your guide to the H E Direct platform",
    category: "Getting Started",
    icon: <UserCog className="w-8 h-8 text-teal-600" />,
    iconBg: "bg-teal-100",
    bgColor: "bg-gradient-to-r from-teal-50 to-cyan-50",
    content: (
      <div className="space-y-6">
        <p className="text-lg text-gray-600">
          As an H E Direct employee, you help students achieve their study abroad dreams. This training covers:
        </p>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gray-100 rounded-xl p-4 flex items-center gap-3">
            <FileText className="w-6 h-6 text-blue-500" />
            <div>
              <p className="font-medium text-gray-800">Applications</p>
              <p className="text-sm text-gray-500">Review & process</p>
            </div>
          </div>
          <div className="bg-gray-100 rounded-xl p-4 flex items-center gap-3">
            <Users className="w-6 h-6 text-green-500" />
            <div>
              <p className="font-medium text-gray-800">Students</p>
              <p className="text-sm text-gray-500">Support & guide</p>
            </div>
          </div>
          <div className="bg-gray-100 rounded-xl p-4 flex items-center gap-3">
            <Building2 className="w-6 h-6 text-purple-500" />
            <div>
              <p className="font-medium text-gray-800">Agents</p>
              <p className="text-sm text-gray-500">Partner support</p>
            </div>
          </div>
          <div className="bg-gray-100 rounded-xl p-4 flex items-center gap-3">
            <GraduationCap className="w-6 h-6 text-orange-500" />
            <div>
              <p className="font-medium text-gray-800">Universities</p>
              <p className="text-sm text-gray-500">Content management</p>
            </div>
          </div>
        </div>
      </div>
    ),
    tips: "Your permissions determine which features you can access - check with your admin!"
  },

  // Slide 2: Understanding Permissions
  {
    title: "Your Permissions",
    subtitle: "Access is role-based",
    category: "Access Control",
    icon: <Shield className="w-8 h-8 text-blue-500" />,
    iconBg: "bg-blue-100",
    bgColor: "bg-gradient-to-r from-blue-500/10 to-cyan-500/10",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">Your admin assigns specific permissions to your account:</p>
        <div className="grid grid-cols-2 gap-3">
          <PermissionCard name="Manage Applications" description="View and update application status" enabled={true} />
          <PermissionCard name="Manage Students" description="Add notes, view profiles" enabled={true} />
          <PermissionCard name="Manage Agents" description="Approve/reject agents" enabled={false} />
          <PermissionCard name="Manage Courses" description="Add/edit course listings" enabled={false} />
          <PermissionCard name="Manage Universities" description="Add partner universities" enabled={false} />
          <PermissionCard name="Manage Payouts" description="Process commission payments" enabled={false} />
          <PermissionCard name="View Analytics" description="Access dashboard analytics" enabled={true} />
        </div>
        <p className="text-sm text-gray-500">Green = Enabled for your role | Grey = Not available</p>
      </div>
    ),
    tips: "Need additional access? Contact your admin to request permission changes."
  },

  // Slide 3: Processing Applications
  {
    title: "Processing Applications",
    subtitle: "Your main workflow",
    category: "Core Tasks",
    icon: <FileText className="w-8 h-8 text-green-500" />,
    iconBg: "bg-green-100",
    bgColor: "bg-gradient-to-r from-green-500/10 to-emerald-500/10",
    content: (
      <div className="space-y-6">
        <StepList steps={[
          { title: "Go to Applications tab", description: "View all pending applications" },
          { title: "Review application details", description: "Check student info and documents" },
          { title: "Verify document completeness", description: "Passport, transcripts, test scores" },
          { title: "Update journey stage", description: "Progress through the 12-stage pipeline" },
          { title: "Add notes", description: "Document your actions for the team" }
        ]} />
      </div>
    ),
    tips: "Always add notes when updating applications - it helps the team track history!"
  },

  // Slide 4: Journey Stage Updates
  {
    title: "Journey Stage Updates",
    subtitle: "Moving applications through the pipeline",
    category: "Core Tasks",
    icon: <Clock className="w-8 h-8 text-purple-500" />,
    iconBg: "bg-purple-100",
    bgColor: "bg-gradient-to-r from-purple-500/10 to-pink-500/10",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">The 12 stages an application moves through:</p>
        <div className="space-y-2">
          <div className="grid grid-cols-4 gap-2 text-xs">
            <div className="bg-blue-100 text-blue-600 p-2 rounded text-center">Inquiry</div>
            <div className="bg-blue-100 text-blue-600 p-2 rounded text-center">Lead Validated</div>
            <div className="bg-blue-100 text-blue-600 p-2 rounded text-center">App Received</div>
            <div className="bg-blue-100 text-blue-600 p-2 rounded text-center">App Submitted</div>
          </div>
          <div className="grid grid-cols-4 gap-2 text-xs">
            <div className="bg-teal-100 text-teal-600 p-2 rounded text-center">Offer Issued</div>
            <div className="bg-teal-100 text-teal-600 p-2 rounded text-center">Offer Accepted</div>
            <div className="bg-teal-100 text-teal-600 p-2 rounded text-center">UCO</div>
            <div className="bg-teal-100 text-teal-600 p-2 rounded text-center">Deposit</div>
          </div>
          <div className="grid grid-cols-4 gap-2 text-xs">
            <div className="bg-green-100 text-green-600 p-2 rounded text-center">CAS Stage</div>
            <div className="bg-green-100 text-green-600 p-2 rounded text-center">Visa Issued</div>
            <div className="bg-green-100 text-green-600 p-2 rounded text-center">Enrolled</div>
            <div className="bg-green-100 text-green-600 p-2 rounded text-center">Commission</div>
          </div>
        </div>
        <p className="text-sm text-gray-500">Blue = Early stages | Yellow = Mid stages | Green = Final stages</p>
      </div>
    ),
    tips: "Verify each milestone before progressing - students and agents receive notifications!"
  },

  // Slide 5: Student Support
  {
    title: "Supporting Students",
    subtitle: "Communication and notes",
    category: "Student Management",
    icon: <Users className="w-8 h-8 text-orange-500" />,
    iconBg: "bg-orange-100",
    bgColor: "bg-gradient-to-r from-orange-500/10 to-amber-500/10",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">How to support students effectively:</p>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gray-100 rounded-xl p-4">
            <MessageSquare className="w-6 h-6 text-blue-500 mb-2" />
            <p className="font-medium text-gray-800">Internal Notes</p>
            <p className="text-sm text-gray-500">Team-only notes for internal tracking</p>
          </div>
          <div className="bg-gray-100 rounded-xl p-4">
            <Bell className="w-6 h-6 text-green-500 mb-2" />
            <p className="font-medium text-gray-800">Student Notes</p>
            <p className="text-sm text-gray-500">Visible to student + sends email</p>
          </div>
        </div>
        <div className="p-3 bg-teal-50 rounded-xl border border-teal-200">
          <p className="text-sm text-teal-600">⚠️ Be careful: Student-visible notes are sent via email!</p>
        </div>
      </div>
    ),
    tips: "Use internal notes for team discussions, student notes for official communications."
  },

  // Slide 6: Agent Support
  {
    title: "Working with Agents",
    subtitle: "Supporting our partners",
    category: "Agent Management",
    icon: <Building2 className="w-8 h-8 text-indigo-500" />,
    iconBg: "bg-indigo-500/20",
    bgColor: "bg-gradient-to-r from-indigo-500/10 to-violet-500/10",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">If you&apos;re assigned as a Point of Contact (POC) for agents:</p>
        <FeatureList items={[
          "Agents can contact you directly via email/phone",
          "Help them with application queries",
          "Guide them on document requirements",
          "Escalate issues to admin when needed",
          "Build strong partner relationships"
        ]} />
        <div className="mt-4 p-4 bg-gray-100 rounded-xl">
          <p className="text-sm text-gray-600">
            <span className="text-teal-600 font-medium">Your role:</span> Be responsive, helpful, and professional. 
            Happy agents = more student referrals!
          </p>
        </div>
      </div>
    ),
    tips: "Respond to agent queries within 24 hours for best partner satisfaction."
  },

  // Slide 7: Document Verification
  {
    title: "Document Verification",
    subtitle: "Checking application documents",
    category: "Quality Control",
    icon: <Eye className="w-8 h-8 text-cyan-500" />,
    iconBg: "bg-cyan-100",
    bgColor: "bg-gradient-to-r from-cyan-500/10 to-blue-500/10",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">Verify these for each application:</p>
        <div className="grid grid-cols-2 gap-3">
          {[
            { check: "Passport validity", desc: "6+ months remaining" },
            { check: "Transcript authenticity", desc: "Official stamp/seal" },
            { check: "English test scores", desc: "Meet minimum requirement" },
            { check: "Document clarity", desc: "Readable, not blurry" },
            { check: "Name consistency", desc: "Same across all docs" },
            { check: "File format", desc: "PDF preferred" },
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3 bg-gray-100 rounded-lg p-3">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
              <div>
                <p className="font-medium text-gray-800 text-sm">{item.check}</p>
                <p className="text-xs text-gray-500">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    ),
    tips: "Request missing documents before moving application to 'Submitted' stage."
  },

  // Slide 8: Using Analytics
  {
    title: "Understanding Analytics",
    subtitle: "Data-driven decisions",
    category: "Analytics",
    icon: <BarChart3 className="w-8 h-8 text-pink-500" />,
    iconBg: "bg-pink-100",
    bgColor: "bg-gradient-to-r from-pink-500/10 to-rose-500/10",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">If you have analytics access, monitor:</p>
        <FeatureList items={[
          "Journey funnel - identify bottlenecks",
          "Conversion rates between stages",
          "Applications by status and country",
          "Agent performance rankings",
          "Processing time at each stage"
        ]} />
        <div className="mt-4 p-3 bg-blue-500/10 rounded-xl border border-blue-500/30">
          <p className="text-sm text-blue-600">📊 Click any funnel stage to filter applications by that stage!</p>
        </div>
      </div>
    ),
    tips: "Review analytics weekly to identify trends and areas for improvement."
  },

  // Slide 9: KPI REQUIREMENTS - B2B
  {
    title: "Your KPI Targets: B2B (Agent-Led)",
    subtitle: "Performance requirements for agent recruitment",
    category: "KPI Requirements",
    icon: <Target className="w-8 h-8 text-teal-600" />,
    iconBg: "bg-teal-100",
    bgColor: "bg-gradient-to-r from-teal-50 to-cyan-50",
    content: (
      <div className="space-y-6">
        <div className="p-4 bg-teal-50 rounded-xl border border-teal-200 text-center">
          <p className="text-sm text-teal-600 mb-1">B2B (Agent-Led) Recruitment Target</p>
          <p className="text-5xl font-semibold text-teal-600">8%</p>
          <p className="text-lg text-gray-800 mt-2">Application to Enrollment Conversion</p>
        </div>
        <p className="text-gray-600">What this means:</p>
        <FeatureList items={[
          "For every 100 applications from your agents, 8 must reach enrollment",
          "Track your agents' progress through the KPI Dashboard",
          "Work with underperforming agents to improve their conversion",
          "Target is measured per intake (Jan, May, Sep)"
        ]} />
        <div className="p-3 bg-red-500/10 rounded-xl border border-red-500/30">
          <p className="text-sm text-red-600 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Missing this target impacts your performance review
          </p>
        </div>
      </div>
    ),
    tips: "Regularly check your KPI dashboard and proactively support struggling agents!"
  },

  // Slide 10: KPI REQUIREMENTS - B2C
  {
    title: "Your KPI Targets: B2C (Direct)",
    subtitle: "Performance requirements for direct student recruitment",
    category: "KPI Requirements",
    icon: <TrendingUp className="w-8 h-8 text-green-500" />,
    iconBg: "bg-green-100",
    bgColor: "bg-gradient-to-r from-green-500/10 to-emerald-500/10",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">B2C (Direct) students require TWO KPIs:</p>
        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 bg-blue-500/10 rounded-xl border border-blue-500/30 text-center">
            <p className="text-sm text-blue-600 mb-1">Inquiry → Lead Validation</p>
            <p className="text-4xl font-semibold text-blue-500">25%</p>
            <p className="text-xs text-gray-500 mt-1">Validate 1 in 4 inquiries</p>
          </div>
          <div className="p-4 bg-green-500/10 rounded-xl border border-green-500/30 text-center">
            <p className="text-sm text-green-600 mb-1">App → Enrollment</p>
            <p className="text-4xl font-semibold text-green-500">12%</p>
            <p className="text-xs text-gray-500 mt-1">Enroll 12 of 100 apps</p>
          </div>
        </div>
        <p className="text-sm text-gray-500">
          <strong className="text-gray-800">Example:</strong> If you receive 100 inquiries, you must validate at least 25 to leads. 
          From 100 applications, at least 12 must reach enrollment.
        </p>
      </div>
    ),
    tips: "B2C requires MORE attention than B2B - follow up quickly with direct inquiries!"
  },

  // Slide 11: KPI Dashboard
  {
    title: "Using the KPI Dashboard",
    subtitle: "Track your performance in real-time",
    category: "KPI Tools",
    icon: <BarChart3 className="w-8 h-8 text-purple-500" />,
    iconBg: "bg-purple-100",
    bgColor: "bg-gradient-to-r from-purple-500/10 to-violet-500/10",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">The KPI Dashboard shows you:</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-gray-100 rounded-lg p-3">
            <p className="font-medium text-gray-800">Your B2B Performance</p>
            <p className="text-xs text-gray-500">Agent-led conversion rate vs 8% target</p>
          </div>
          <div className="bg-gray-100 rounded-lg p-3">
            <p className="font-medium text-gray-800">Your B2C Performance</p>
            <p className="text-xs text-gray-500">Direct conversion rates vs targets</p>
          </div>
          <div className="bg-gray-100 rounded-lg p-3">
            <p className="font-medium text-gray-800">Agents You Manage</p>
            <p className="text-xs text-gray-500">Performance of your POC agents</p>
          </div>
          <div className="bg-gray-100 rounded-lg p-3">
            <p className="font-medium text-gray-800">Intake Progress</p>
            <p className="text-xs text-gray-500">Current intake KPI tracking</p>
          </div>
        </div>
        <p className="text-sm text-gray-500">
          KPIs are refreshed each intake period (January, May, September). 
          Start fresh each intake with renewed targets!
        </p>
      </div>
    ),
    tips: "Check your KPI dashboard daily - early intervention prevents missed targets!"
  },

  // Slide: Apply for Students (NEW FEATURE)
  {
    title: "Apply for Courses on Behalf of Students",
    subtitle: "Submit applications for students directly",
    category: "New Feature",
    icon: <GraduationCap className="w-8 h-8 text-teal-600" />,
    iconBg: "bg-teal-100",
    bgColor: "bg-gradient-to-r from-teal-50 to-cyan-50",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">If you have the right permissions, you can apply for courses on behalf of students:</p>
        <StepList steps={[
          { title: "Go to Students tab", description: "View all registered students" },
          { title: "Click 'Apply' button", description: "Next to any student in the Actions column" },
          { title: "Search for courses", description: "Filter by name, university, or field of study" },
          { title: "Click 'Apply' on a course", description: "Application is created immediately" }
        ]} />
        <div className="mt-4 p-3 bg-teal-50 rounded-lg border border-teal-200">
          <p className="text-sm text-teal-700">💡 This is useful for walk-in students or phone inquiries where quick action is needed.</p>
        </div>
      </div>
    ),
    tips: "Check with your admin if you need permission to use this feature."
  },

  // Slide 12: Best Practices
  {
    title: "Best Practices",
    subtitle: "Tips for excellence",
    category: "Guidelines",
    icon: <Settings className="w-8 h-8 text-cyan-500" />,
    iconBg: "bg-cyan-100",
    bgColor: "bg-gradient-to-r from-cyan-500/10 to-blue-500/10",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">Follow these guidelines:</p>
        <FeatureList items={[
          "Document every action with notes",
          "Respond to queries within 24 hours",
          "Double-check before updating stages",
          "Escalate complex issues to admin",
          "Keep student data confidential",
          "Be professional in all communications"
        ]} />
      </div>
    ),
    tips: "When in doubt, ask a colleague or your supervisor!"
  },

  // Slide 13: Scholarship Management
  {
    title: "Scholarship Management",
    subtitle: "Help students find funding opportunities",
    category: "Scholarships",
    icon: <Award className="w-8 h-8 text-teal-600" />,
    iconBg: "bg-teal-100",
    bgColor: "bg-gradient-to-r from-yellow-500/10 to-amber-500/10",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">The Scholarships tab lets you manage funding opportunities:</p>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gray-100 rounded-xl p-4 border-l-4 border-yellow-500">
            <PlusCircle className="w-6 h-6 text-teal-600 mb-2" />
            <p className="font-medium text-gray-800">Add Scholarships</p>
            <p className="text-sm text-gray-500">Add GREAT, Chevening, Commonwealth, and country-specific scholarships</p>
          </div>
          <div className="bg-gray-100 rounded-xl p-4 border-l-4 border-purple-500">
            <Sparkles className="w-6 h-6 text-purple-500 mb-2" />
            <p className="font-medium text-gray-800">AI Discovery</p>
            <p className="text-sm text-gray-500">Use AI to find scholarships: Free study in Italy or German DAAD</p>
          </div>
        </div>
        <FeatureList items={[
          "Add scholarships from Scholarships tab",
          "Use AI Fetch for quick discovery",
          "Set featured status for popular scholarships",
          "All student applications come to H E Direct team",
          "Review student interests in Admin panel"
        ]} />
      </div>
    ),
    tips: "Popular scholarships: Chevening, GREAT, Commonwealth, Fulbright, DAAD, Erasmus Mundus"
  },

  // Slide 14: Search & Policy Documents
  {
    title: "Search & Policy Documents",
    subtitle: "New features for employees",
    category: "New Features",
    icon: <Shield className="w-8 h-8 text-purple-500" />,
    iconBg: "bg-purple-100",
    bgColor: "bg-gradient-to-r from-purple-500/10 to-pink-500/10",
    content: (
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-3">
            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
              <Eye className="w-5 h-5 text-cyan-500" /> Search Feature
            </h3>
            <FeatureList items={[
              "Search students by name, email, ID",
              "Search agents by name, company",
              "Results update as you type",
              "Click X to clear search"
            ]} />
          </div>
          <div className="space-y-3">
            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
              <Shield className="w-5 h-5 text-purple-500" /> Policy Documents
            </h3>
            <FeatureList items={[
              "Access from Training Hub",
              "Partner Risk Strategy",
              "Student Protection Guidelines",
              "Confidential - employees only"
            ]} />
          </div>
        </div>
        <div className="mt-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
          <p className="text-sm text-purple-700">📄 Policy documents are available at the bottom of the Training Hub page (employees only).</p>
        </div>
      </div>
    ),
    tips: "Review policy documents regularly - they contain important guidelines for student protection!"
  },

  // Slide 15: Attendance System
  {
    title: "Marking Your Attendance",
    subtitle: "Daily check-in and check-out",
    category: "Attendance",
    icon: <Clock className="w-8 h-8 text-blue-500" />,
    iconBg: "bg-blue-100",
    bgColor: "bg-gradient-to-r from-blue-50 to-indigo-50",
    content: (
      <div className="space-y-6">
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <p className="font-semibold text-blue-800 mb-2">📍 Office Hours (Pakistan Standard Time)</p>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-600">Working Days:</p>
              <p className="font-medium text-gray-800">Monday to Saturday</p>
            </div>
            <div>
              <p className="text-gray-600">Timing:</p>
              <p className="font-medium text-gray-800">10:00 AM - 6:00 PM</p>
            </div>
          </div>
        </div>
        <div className="space-y-3">
          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-green-500" /> Office Locations
          </h3>
          <div className="grid gap-3">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="font-medium text-gray-800">Head Office - Peshawar</p>
              <p className="text-xs text-gray-500">Rafeys Height, 2nd Floor, Flat 306, Abdara Road, Near MM Academy</p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="font-medium text-gray-800">Regional Office - Islamabad</p>
              <p className="text-xs text-gray-500">Office No 1, 3rd Floor, Silver City Plaza, G11 Markaz, Sachal Sarmast Road</p>
            </div>
          </div>
        </div>
      </div>
    ),
    tips: "You must be within 200 meters of an office location to mark 'Present' status. Remote check-ins are tracked separately."
  },

  // Slide 16: How to Mark Attendance
  {
    title: "How to Check In",
    subtitle: "Step-by-step attendance guide",
    category: "Attendance",
    icon: <Timer className="w-8 h-8 text-teal-500" />,
    iconBg: "bg-teal-100",
    bgColor: "bg-gradient-to-r from-teal-50 to-emerald-50",
    content: (
      <div className="space-y-6">
        <StepList steps={[
          { 
            title: "Go to My Attendance Tab", 
            description: "Find it in your dashboard navigation" 
          },
          { 
            title: "Allow Location Access", 
            description: "Your browser will ask for location permission - click 'Allow'" 
          },
          { 
            title: "Click 'Check In' Button", 
            description: "Available anytime on working days until 6:15 PM PKT" 
          },
          { 
            title: "View Your Status", 
            description: "See if you're marked as 'Present' (at office) or 'Remote'" 
          },
          { 
            title: "Check Out Before Leaving", 
            description: "Click 'Check Out' at end of work to record total hours" 
          }
        ]} />
        <div className="grid grid-cols-2 gap-4 mt-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
            <p className="text-2xl mb-1">✅</p>
            <p className="font-medium text-green-700">Present</p>
            <p className="text-xs text-gray-500">At office location</p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
            <p className="text-2xl mb-1">🌐</p>
            <p className="font-medium text-blue-700">Remote</p>
            <p className="text-xs text-gray-500">Outside office area</p>
          </div>
        </div>
      </div>
    ),
    tips: "Always check out at the end of your shift - it helps track your working hours accurately!"
  },

  // Slide 17: Attendance Rules
  {
    title: "Attendance Guidelines",
    subtitle: "Important rules to follow",
    category: "Attendance",
    icon: <Calendar className="w-8 h-8 text-orange-500" />,
    iconBg: "bg-orange-100",
    bgColor: "bg-gradient-to-r from-orange-50 to-amber-50",
    content: (
      <div className="space-y-6">
        <div className="space-y-3">
          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-500" /> Do&apos;s
          </h3>
          <FeatureList items={[
            "Check in as soon as you arrive at office",
            "Allow location access for accurate tracking",
            "Check out before leaving for the day",
            "Review your attendance history regularly",
            "Report any technical issues to admin"
          ]} />
        </div>
        <div className="space-y-3">
          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500" /> Important Notes
          </h3>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-2">
            <p className="text-sm text-red-700">⚠️ Check-in closes at 6:15 PM PKT - early arrivals welcome!</p>
            <p className="text-sm text-red-700">⚠️ Sunday is a holiday - no attendance marking</p>
            <p className="text-sm text-red-700">⚠️ Location must be enabled for check-in to work</p>
            <p className="text-sm text-red-700">⚠️ Late check-ins (after 10:30 AM) are tracked separately</p>
          </div>
        </div>
      </div>
    ),
    tips: "Your attendance record is visible to administrators. Maintain consistency for a good track record!"
  },

  // Slide 18: Summary
  {
    title: "You're Ready!",
    subtitle: "Welcome to the team",
    category: "Summary",
    icon: <CheckCircle className="w-8 h-8 text-green-500" />,
    iconBg: "bg-green-100",
    bgColor: "bg-gradient-to-r from-green-500/10 to-emerald-500/10",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">Daily checklist:</p>
        <FeatureList items={[
          "✅ Check in when you arrive at office",
          "Check pending applications",
          "Review new student submissions",
          "Respond to agent queries (if POC)",
          "Update journey stages as needed",
          "Use Search to find specific users quickly",
          "Check scholarship inquiries from students",
          "Review Policy Documents periodically",
          "Check your monthly fee targets",
          "Monitor your Saving Pot balance",
          "✅ Check out before leaving"
        ]} />
        <div className="mt-6 p-4 bg-teal-50 rounded-xl border border-teal-200">
          <p className="text-teal-600 font-medium">📞 Need Help?</p>
          <p className="text-gray-600 text-sm mt-1">Contact: WhatsApp +92 326 8096371 | Email: admin.team@hedirect.org</p>
        </div>
      </div>
    ),
    tips: "You can restart this training anytime from the Training Hub!"
  },

  // Slide: My Saving Pot
  {
    title: "My Saving Pot",
    subtitle: "Track your commission earnings",
    category: "New Feature",
    icon: <DollarSign className="w-8 h-8 text-green-500" />,
    iconBg: "bg-green-100",
    bgColor: "bg-gradient-to-r from-green-50 to-emerald-50",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">When you receive large payments, a portion is credited to your Saving Pot:</p>
        <FeatureList items={[
          "View your current Saving Pot balance",
          "See history of credits (from large payments)",
          "Track payouts processed by admin",
          "Request payout when ready"
        ]} />
        <div className="p-4 bg-green-50 rounded-xl border border-green-200">
          <p className="text-sm text-green-700">💰 Your Saving Pot grows with each qualifying payment you process!</p>
        </div>
      </div>
    ),
    tips: "Check your Saving Pot regularly from the My Saving Pot section on your dashboard."
  },

  // Slide: My Target
  {
    title: "My Monthly Target",
    subtitle: "Track your fee collection goals",
    category: "New Feature",
    icon: <Target className="w-8 h-8 text-orange-500" />,
    iconBg: "bg-orange-100",
    bgColor: "bg-gradient-to-r from-orange-50 to-amber-50",
    content: (
      <div className="space-y-6">
        <p className="text-gray-600">Admin sets monthly fee collection targets for staff:</p>
        <FeatureList items={[
          "View your target amount for the current month",
          "Track your progress (collected vs target)",
          "See percentage completion",
          "Target resets each month"
        ]} />
        <div className="grid grid-cols-2 gap-4 mt-4">
          <div className="bg-orange-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-orange-600">£5,000</p>
            <p className="text-xs text-gray-500">Example Target</p>
          </div>
          <div className="bg-green-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-green-600">£3,200</p>
            <p className="text-xs text-gray-500">Collected (64%)</p>
          </div>
        </div>
      </div>
    ),
    tips: "Check the My Target panel on your dashboard to stay on track!"
  },

  // === NEW FEATURES (2026) ===
  // Slide: AI Visa Predictor + Needs Review Queue
  {
    title: "AI Visa Predictor + Needs Review Queue",
    subtitle: "Triage at-risk students fast",
    category: "New • AI",
    icon: <Sparkles className="w-8 h-8 text-indigo-600" />,
    iconBg: "bg-indigo-100",
    bgColor: "bg-gradient-to-r from-indigo-50 to-violet-50",
    content: (
      <div className="space-y-5">
        <p className="text-gray-600">
          Every application is auto-scored for visa success probability. Anything below
          60% (or critical flag) lands in your <strong>Needs Review</strong> queue.
        </p>
        <FeatureList items={[
          "Applications tab → filter 'Needs Review only'",
          "Open the application → Visa Probability panel for AI notes",
          "Add a counsellor note to address the flag",
          "Cleared cases drop off the queue automatically"
        ]} />
      </div>
    ),
    tips: "Aim for an empty queue by end of day. It dramatically improves admit rates."
  },

  // Slide: Inline Document Approval
  {
    title: "Inline Document Approval",
    subtitle: "Approve / reject without page switching",
    category: "Workflow",
    icon: <CheckCircle className="w-8 h-8 text-green-600" />,
    iconBg: "bg-green-100",
    bgColor: "bg-gradient-to-r from-green-50 to-emerald-50",
    content: (
      <div className="space-y-5">
        <p className="text-gray-600">
          Student & journey documents now have inline Approve / Reject controls. A
          checklist banner shows what's still missing per destination.
        </p>
        <FeatureList items={[
          "Open Student → Documents tab",
          "Preview, then click Approve or Reject inline",
          "Reject reasons are emailed to the student & agent",
          "Banner ticks off requirements automatically"
        ]} />
      </div>
    ),
    tips: "Always give a specific reject reason — vague reasons cause re-uploads of the same wrong file."
  },

  // Slide: Smart Matching for Counselling
  {
    title: "Smart Matching for Counselling",
    subtitle: "Better course recommendations to share with students",
    category: "Enhanced",
    icon: <Award className="w-8 h-8 text-emerald-600" />,
    iconBg: "bg-emerald-100",
    bgColor: "bg-gradient-to-r from-emerald-50 to-teal-50",
    content: (
      <div className="space-y-5">
        <p className="text-gray-600">
          Course Search now factors in full academic profile + English test equivalence
          to rank courses by realistic admit odds.
        </p>
        <FeatureList items={[
          "Complete the student's academic profile fully",
          "System converts any English test to IELTS internally",
          "Top 3 matches highlighted — share these in your counselling call",
          "Use the 'why not' tooltip on rejected matches"
        ]} />
      </div>
    ),
    tips: "5 minutes filling the profile saves 30 minutes of bad recommendations later."
  },

  // Slide: Record Student Payments
  {
    title: "Record Student Payments",
    subtitle: "Log fee payments quickly and accurately",
    category: "Tools",
    icon: <DollarSign className="w-8 h-8 text-blue-600" />,
    iconBg: "bg-blue-100",
    bgColor: "bg-gradient-to-r from-blue-50 to-cyan-50",
    content: (
      <div className="space-y-5">
        <p className="text-gray-600">
          Use the new Record Payment dialog under Student → Payments to log every
          incoming fee. Allocation rules update your monthly target automatically.
        </p>
        <FeatureList items={[
          "Click 'Record Payment' on the student's profile",
          "Enter amount, currency, method, reference number",
          "Choose the application this payment is for",
          "Confirmation email fires to the payer automatically"
        ]} />
      </div>
    ),
    tips: "Record same-day. Late entries throw off monthly target reports."
  },

  // Slide: Persistent Document Storage
  {
    title: "Documents Persist Across Deploys",
    subtitle: "No more lost files",
    category: "Reliability",
    icon: <Shield className="w-8 h-8 text-purple-600" />,
    iconBg: "bg-purple-100",
    bgColor: "bg-gradient-to-r from-purple-50 to-violet-50",
    content: (
      <div className="space-y-5">
        <p className="text-gray-600">
          All uploaded documents now live in encrypted persistent cloud storage.
          Restarts, redeploys and pod replacements no longer wipe any files.
        </p>
        <FeatureList items={[
          "Upload from Student or Application pages as before",
          "Files keep their original filename + extension",
          "Re-uploads only required for very old (pre-rollout) documents",
          "Download links work indefinitely"
        ]} />
      </div>
    ),
    tips: "If a legacy file is missing, ask the student to re-upload — admins can also bulk re-upload."
  }
];

export default employeeTrainingSlides;
